import type {
  ExportData,
  IDataTransferService,
  ImportValidationResult,
} from '@/domain/precon-supporter/services/IDataTransferService';
import type { LearningProgressRepository } from '@/domain/precon-supporter/learning-progress';
import type { LearnerProfileRepository } from '@/domain/precon-supporter/learner-profile';
import { LearningProgress } from '@/domain/precon-supporter/learning-progress';
import { LearnerProfile } from '@/domain/precon-supporter/learner-profile';
import {
  CourseId,
  LectureProgress,
} from '@/domain/precon-supporter/value-objects';
import {
  CURRENT_VERSION,
  isKnownVersion,
  migrateToLatest,
  needsMigration,
} from './migrations';

/**
 * データ転送サービスの実装
 *
 * IndexedDBのデータをエクスポート/インポートする。
 */
export class DataTransferService implements IDataTransferService {
  constructor(
    private readonly learningProgressRepository: LearningProgressRepository,
    private readonly learnerProfileRepository: LearnerProfileRepository
  ) {}

  /**
   * 全データをエクスポート用形式に変換
   */
  async exportAllData(): Promise<ExportData> {
    // 学習進捗を全件取得
    const learningProgresses = await this.learningProgressRepository.findAll();

    // 受講者プロフィールを取得
    const learnerProfile = await this.learnerProfileRepository.find();

    return {
      version: CURRENT_VERSION,
      exportedAt: new Date().toISOString(),
      learningProgresses: learningProgresses.map(progress => progress.toData()),
      learnerProfile: learnerProfile ? learnerProfile.toData() : null,
    };
  }

  /**
   * インポートデータを検証し、必要に応じてマイグレーションを実行
   */
  validateAndMigrateImportData(data: unknown): ImportValidationResult {
    // 基本的な構造チェック
    if (!data || typeof data !== 'object') {
      return { success: false, error: 'データの形式が不正です。' };
    }

    const obj = data as Record<string, unknown>;

    // バージョンチェック
    if (typeof obj.version !== 'number') {
      return { success: false, error: 'バージョン情報がありません。' };
    }

    const originalVersion = obj.version;

    // 未来のバージョンチェック
    if (obj.version > CURRENT_VERSION) {
      return {
        success: false,
        error: `このファイルは新しいバージョン（v${obj.version}）で作成されています。アプリケーションを更新してください。`,
      };
    }

    // 古すぎるバージョンチェック（バージョン1未満）
    if (obj.version < 1) {
      return {
        success: false,
        error: 'サポートされていない古いバージョンのデータです。',
      };
    }

    // 既知のバージョンかどうかチェック
    if (!isKnownVersion(data)) {
      return {
        success: false,
        error: `不明なバージョン（v${obj.version}）のデータです。`,
      };
    }

    // マイグレーションが必要な場合は実行
    let migratedData = data;
    const migrated = needsMigration(data);

    if (migrated) {
      try {
        migratedData = migrateToLatest(data);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'マイグレーションに失敗しました。';
        return { success: false, error: message };
      }
    }

    // マイグレーション後のデータを詳細検証
    if (!this.validateImportData(migratedData)) {
      return {
        success: false,
        error:
          'データの内容が不正です。ファイルが破損している可能性があります。',
      };
    }

    return {
      success: true,
      data: migratedData as ExportData,
      migrated,
      originalVersion,
    };
  }

  /**
   * インポートデータを検証（後方互換性のため残す）
   * @deprecated validateAndMigrateImportData を使用してください
   */
  validateImportData(data: unknown): data is ExportData {
    if (!data || typeof data !== 'object') {
      return false;
    }

    const obj = data as Record<string, unknown>;

    // version チェック
    if (typeof obj.version !== 'number' || obj.version < 1) {
      return false;
    }

    // exportedAt チェック
    if (typeof obj.exportedAt !== 'string') {
      return false;
    }

    // learningProgresses チェック
    if (!Array.isArray(obj.learningProgresses)) {
      return false;
    }

    // 各 learningProgress の検証
    for (const progress of obj.learningProgresses) {
      if (!this.validateLearningProgressData(progress)) {
        return false;
      }
    }

    // learnerProfile チェック（null または有効なオブジェクト）
    if (obj.learnerProfile !== null) {
      if (!this.validateLearnerProfileData(obj.learnerProfile)) {
        return false;
      }
    }

    return true;
  }

  /**
   * データをインポート（既存データを上書き）
   */
  async importAllData(data: ExportData): Promise<void> {
    // 既存データを削除
    const existingProgresses = await this.learningProgressRepository.findAll();
    for (const progress of existingProgresses) {
      await this.learningProgressRepository.delete(progress.getId());
    }
    await this.learnerProfileRepository.delete();

    // 学習進捗をインポート
    for (const progressData of data.learningProgresses) {
      const lectureProgresses = progressData.lectureProgresses.map(p =>
        LectureProgress.reconstruct(
          p.lectureId,
          p.completed,
          p.completedAt ? new Date(p.completedAt) : null
        )
      );

      const learningProgress = LearningProgress.reconstruct({
        id: progressData.id,
        courseId: CourseId.fromString(progressData.courseId),
        lectureProgresses,
        examinationPassed: progressData.examinationPassed,
        createdAt: new Date(progressData.createdAt),
        updatedAt: new Date(progressData.updatedAt),
        passerToken: progressData.passerToken,
      });

      await this.learningProgressRepository.save(learningProgress);
    }

    // 受講者プロフィールをインポート
    if (data.learnerProfile) {
      const learnerProfile = LearnerProfile.reconstruct({
        wantsAdvancedCourse: data.learnerProfile.wantsAdvancedCourse,
        nickname: data.learnerProfile.nickname,
        createdAt: new Date(data.learnerProfile.createdAt),
        updatedAt: new Date(data.learnerProfile.updatedAt),
      });

      await this.learnerProfileRepository.save(learnerProfile);
    }
  }

  /**
   * 学習進捗データの検証
   */
  private validateLearningProgressData(data: unknown): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }

    const obj = data as Record<string, unknown>;

    // 必須フィールドのチェック
    if (typeof obj.id !== 'string') return false;
    if (typeof obj.courseId !== 'string') return false;
    if (!Array.isArray(obj.lectureProgresses)) return false;
    if (typeof obj.examinationPassed !== 'boolean') return false;
    if (typeof obj.createdAt !== 'string') return false;
    if (typeof obj.updatedAt !== 'string') return false;

    // courseId の値チェック
    if (obj.courseId !== 'basic' && obj.courseId !== 'advanced') {
      return false;
    }

    // lectureProgresses の各要素を検証
    for (const lecture of obj.lectureProgresses) {
      if (!this.validateLectureProgressData(lecture)) {
        return false;
      }
    }

    // passerToken はオプショナル
    if (obj.passerToken !== undefined && typeof obj.passerToken !== 'string') {
      return false;
    }

    return true;
  }

  /**
   * 講座進捗データの検証
   */
  private validateLectureProgressData(data: unknown): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }

    const obj = data as Record<string, unknown>;

    if (typeof obj.lectureId !== 'string') return false;
    if (typeof obj.completed !== 'boolean') return false;
    if (obj.completedAt !== null && typeof obj.completedAt !== 'string') {
      return false;
    }

    return true;
  }

  /**
   * 受講者プロフィールデータの検証
   */
  private validateLearnerProfileData(data: unknown): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }

    const obj = data as Record<string, unknown>;

    if (typeof obj.wantsAdvancedCourse !== 'boolean') return false;
    if (obj.nickname !== null && typeof obj.nickname !== 'string') {
      return false;
    }
    if (typeof obj.createdAt !== 'string') return false;
    if (typeof obj.updatedAt !== 'string') return false;

    return true;
  }
}
